import { message } from "antd";
import axiosInstance from "../../../helpers/axios";
import { responseDataFormate } from "../../../helpers/bycrypt";
import { API_URL } from "../../../helpers/utils";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

export interface DepartmentFilterParams {
  search?: string;
  page?: number;
  limit?: number;
  id?: string;
  sortBy?: string;
  sortOrder?: string;
}

interface DepartmentAPIParams {
  filters: DepartmentFilterParams;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const GetDepartmentListAPI =
  ({ filters, onSuccess, onError }: DepartmentAPIParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.get(`${API_URL}/departments`, {
          params: filters,
        });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          onSuccess?.(result.data);
        } else {
          message.error(result?.message || "Failed to fetch frame options list");
          onError?.();
        }
      } catch (error: any) {
        console.error("Fetch department error:", error);
        onError?.();
        message.error(error?.response?.data?.message || "Unable to fetch data");
      }
    };

export interface CategoryFilterParams {
  search?: string;
  page?: number;
  limit?: number;
  id?: string;
  sortBy?: string;
  sortOrder?: string;
  product_type?: any[];
}

interface CategoryAPIParams {
  filters: CategoryFilterParams;
  onSuccess: (data: any) => void;
  onError: () => void;
}

// 2. Get Category List API
export const GetCategoryListAPI =
  ({ filters, onSuccess, onError }: CategoryAPIParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.get(`${API_URL}/categories`, {
          params: filters,
        });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          onSuccess?.(result.data);
        } else {
          message.error(result?.message || "Failed to fetch category list");
          onError?.();
        }
      } catch (error: any) {
        console.error("Fetch category error:", error);
        onError?.();
        message.error(error?.response?.data?.message || "Unable to fetch data");
      }
    };

export interface MachineFilterParams {
  search?: string;
  page?: number;
  limit?: number;
  id?: string;
  department?: string[];
  sortBy?: string;
  sortOrder?: string;
}

interface MachineAPIParams {
  filters: MachineFilterParams;
  onSuccess: (data: any) => void;
  onError: () => void;
}

// 3. Get Machine List API
export const GetMachineListAPI =
  ({ filters, onSuccess, onError }: MachineAPIParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.get(`${API_URL}/machines`, {
          params: filters,
        });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          onSuccess?.(result.data);
        } else {
          message.error(result?.message || "Failed to fetch machine list");
          onError?.();
        }
      } catch (error: any) {
        console.error("Fetch machine error:", error);
        onError?.();
        message.error(error?.response?.data?.message || "Unable to fetch data");
      }
    };

interface CreateProductParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const createProductAPI =
  ({ payload, onSuccess, onError }: CreateProductParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.post(`${API_URL}/products`, payload);

        let result = responseDataFormate(response.data);

        if (result?.success) {
          message.success(result?.message || "Product created successfully");
          onSuccess?.(result.data);
        } else {
          message.error(result?.message || "Failed to create Product");
          onError?.();
        }
      } catch (error: any) {
        console.error("Create Product error:", error);
        onError?.();

        const errorMessage =
          error?.response?.data?.message || "Unable to create Product";
        message.error(errorMessage);
      }
    };

export interface ProductFilterParams {
  master_product?: string;
  page?: number;
  limit?: number;
  category?: string[];
  department?: string;
  machine?: string;
  product_type?: string;
  is_active?: string;
  search?: string;
  id?: string;
  sortBy?: string | null;
  sortOrder?: string | null;
  with_price?: boolean;
  //For getting accessories info
  key?: string;
  quote_id?: string;
  component_type?: string;
  variant_check?: boolean;
}

interface GetProductsListParams {
  filters: ProductFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const getProductsListAPI =
  ({ filters, onSuccess, onError, signal }: GetProductsListParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.get("/products", {
          params: filters,
          signal: signal,
        });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          onSuccess?.(result.data);
        } else {
          message.error(result?.message || "Failed to fetch Products");
          onError?.();
        }
      } catch (error: any) {
        if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
        console.error("Get Products List Error:", error);
        message.error(
          error?.response?.data?.message || "Unable to fetch products",
        );
        onError?.();
      }
    };

export const fetchProductsQueryFn = async (
  filters: ProductFilterParams,
  signal?: AbortSignal,
) => {
  const response = await axiosInstance.get("/products", {
    params: filters,
    signal: signal,
  });

  let result = responseDataFormate(response.data);

  if (result?.success) {
    return result.data;
  } else {
    throw new Error(result?.message || "Failed to fetch Products");
  }
};

interface DeleteProductParams {
  id: string;
  onSuccess: () => void;
  onError: () => void;
}

export const deleteProductAPI =
  ({ id, onSuccess, onError }: DeleteProductParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.delete(`${API_URL}/products`, {
          data: { id: id },
        });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          message.success(result?.message || "Product deleted successfully");
          onSuccess?.();
        } else {
          message.error(result?.message || "Failed to delete Product");
          onError?.();
        }
      } catch (error: any) {
        console.error("Delete Product error:", error);
        onError?.();

        const errorMessage =
          error?.response?.data?.message || "Unable to delete Product";
        message.error(errorMessage);
      }
    };

interface UpdateProductParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
  onProductDataMissingError?: () => void;
}

export const updateProductAPI =
  ({
    payload,
    onSuccess,
    onError,
    onProductDataMissingError,
  }: UpdateProductParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.put(`${API_URL}/products`, payload);

        let result = responseDataFormate(response.data);

        if (result?.success) {
          message.success(result?.message || "Product updated successfully");
          onSuccess?.(result.data);
        } else {
          message.error(result?.message || "Failed to update Product");
          onError?.();
        }
      } catch (error: any) {
        console.error("Update Product error:", error);
        onError?.();

        const status = error?.response?.status;
        const errorMessage = error?.response?.data?.message;
        if (status === 422) {
          if (onProductDataMissingError) {
            onProductDataMissingError();
          }
        } else {
          // Handle all other errors (500, 400, etc.)
          message.error(errorMessage || "Something went wrong");
        }
      }
    };

export interface VariantFilterParams {
  finish?: string;
  color?: string;
  is_active?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string | null;
  sortOrder?: string | null;
  search?: string;
}

interface GetVariantListParams {
  product_id: string;
  filters: VariantFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const getVariantListAPI =
  ({ product_id, filters, onSuccess, onError, signal }: GetVariantListParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.get(
          `${API_URL}/product-variants/product/${product_id}`,
          {
            params: filters,
            signal: signal,
          },
        );

        let result = responseDataFormate(response.data);

        if (result?.success) {
          onSuccess?.(result.data);
        } else {
          message.error(result?.message || "Failed to fetch Variants");
          onError?.();
        }
      } catch (error: any) {
        if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
        console.error("Get Variant List Error:", error);
        message.error(
          error?.response?.data?.message || "Unable to fetch variants",
        );
        onError?.();
      }
    };

// Query function for react-query
export const fetchVariantsQueryFn = async (
  product_id: string,
  filters: any,
  signal?: AbortSignal,
) => {
  try {
    const response = await axiosInstance.get(
      `${API_URL}/product-variants/product/${product_id}`,
      {
        params: filters,
        signal: signal,
      },
    );

    const result = responseDataFormate(response.data);

    if (result?.success) {
      return result.data;
    } else {
      throw new Error(result?.message || "Failed to fetch Variants");
    }
  } catch (error: any) {
    if (error.code === "ERR_CANCELED" || error.name === "AbortError") {
      throw error;
    }
    console.error("Get Variant List Error:", error);
    throw new Error(
      error?.response?.data?.message || "Unable to fetch variants",
    );
  }
};

interface UpdateVariantParams {
  variant_id: string;
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const updateVariantAPI =
  ({ variant_id, payload, onSuccess, onError }: UpdateVariantParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.put(
          `${API_URL}/product-variants/${variant_id}`,
          payload,
        );

        let result = responseDataFormate(response.data);

        if (result?.success) {
          message.success(result?.message || "Variant updated successfully");
          onSuccess?.(result.data);
        } else {
          message.error(result?.message || "Failed to update Variant");
          onError?.();
        }
      } catch (error: any) {
        console.error("Update Variant error:", error);
        onError?.();

        const errorMessage =
          error?.response?.data?.message || "Unable to update Variant";
        message.error(errorMessage);
      }
    };

export const priceSheetVariantToggleAPI =
  ({ payload, onSuccess, onError }: UpdateVariantParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.put(
          `/price-sheets/variants/toggle-active`,
          payload,
        );

        let result = responseDataFormate(response.data);

        if (result?.success) {
          message.success(result?.message || "Variant updated successfully");
          onSuccess?.(result.data);
        } else {
          message.error(result?.message || "Failed to update Variant");
          onError?.();
        }
      } catch (error: any) {
        console.error("Update Variant error:", error);
        onError?.();

        const errorMessage =
          error?.response?.data?.message || "Unable to update Variant";
        message.error(errorMessage);
      }
    };

interface DeleteVariantParams {
  variant_id: string;
  price_id?: string;
  onSuccess: () => void;
  onError: () => void;
}

export const deleteVariantAPI =
  ({ variant_id, price_id, onSuccess, onError }: DeleteVariantParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.delete(
          `${API_URL}/product-variants/${variant_id}`,
          {
            data: { price_id: price_id },
          },
        );

        let result = responseDataFormate(response.data);

        if (result?.success) {
          message.success(result?.message || "Variant deleted successfully");
          onSuccess?.();
        } else {
          message.error(result?.message || "Failed to delete Variant");
          onError?.();
        }
      } catch (error: any) {
        console.error("Delete Variant error:", error);
        onError?.();

        const errorMessage =
          error?.response?.data?.message || "Unable to delete Variant";
        message.error(errorMessage);
      }
    };

interface UpdateProductVariantsParams {
  variant_id: string;
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const updateProductVariantsAPI =
  ({ variant_id, payload, onSuccess, onError }: UpdateProductVariantsParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.put(
          `/product-variants/${variant_id}`,
          payload,
        );

        let result = responseDataFormate(response.data);

        if (result?.success) {
          message.success(
            result?.message || "Product Variant updated successfully",
          );
          onSuccess?.(result.data);
        } else {
          message.error(result?.message || "Failed to update Product Variant");
          onError?.();
        }
      } catch (error: any) {
        console.error("Update Product Variant error:", error);
        onError?.();

        const errorMessage =
          error?.response?.data?.message || "Unable to update Product Variant";
        message.error(errorMessage);
      }
    };

export interface FramePartsOptionsFilterParams {
  product_id?: string;
}

interface FramePartsOptionsAPI {
  filters: FramePartsOptionsFilterParams;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const GetFramePartOptionsAPI =
  ({ filters, onSuccess, onError }: FramePartsOptionsAPI) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.get(`/frame-parts/distinct-values`, {
          params: filters,
        });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          onSuccess?.(result.data);
        } else {
          message.error(result?.message || "Failed to fetch frame options list");
          onError?.();
        }
      } catch (error: any) {
        console.error("Fetch frame options error:", error);
        onError?.();
        message.error(error?.response?.data?.message || "Unable to fetch data");
      }
    };

interface ProductTypesAPI {
  params?: {
    isEstimation?: boolean;
    [key: string]: any;
  };
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const GetProductTypesAPI =
  ({ params, onSuccess, onError }: ProductTypesAPI) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.get(`/product-types`, { params });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          onSuccess?.(result.data);
        } else {
          message.error(result?.message);
          onError?.();
        }
      } catch (error: any) {
        console.error("Fetch product types error:", error);
        onError?.();
        message.error(error?.response?.data?.message);
      }
    };

interface GetAllVariantListParams {
  filters: {
    id?: string;
    product_id?: string;
    category?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    component_type?: string;
    type?: string;
    last_sync_date?: string | null;
  };
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
  silentError?: boolean;
}

export const getAllVariantListAPI =
  ({
    filters,
    onSuccess,
    onError,
    signal,
    silentError,
  }: GetAllVariantListParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.get(`/product-variants`, {
          params: filters,
          signal: signal,
        });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          onSuccess?.(result.data);
        } else {
          if (!silentError) {
            message.error(result?.message || "Failed to fetch Variants");
          }
          onError?.();
        }
      } catch (error: any) {
        if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
        console.error("Get Variant List Error:", error);
        if (!silentError) {
          message.error(
            error?.response?.data?.message || "Unable to fetch variants",
          );
        }
        onError?.();
      }
    };

interface ExportProductVariantsParams {
  id: string;
  onSuccess?: () => void;
  onError?: () => void;
}

export const exportProductVariantsAPI =
  ({ id, onSuccess, onError }: ExportProductVariantsParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.get(
          `/products/${id}/export-variants`,
        );

        let result = responseDataFormate(response.data);

        if (result?.success) {
          message.success(result?.message || "Export initiated successfully");
          onSuccess?.();
        } else {
          message.error(result?.message || "Failed to export variants");
          onError?.();
        }
      } catch (error: any) {
        console.error("Export error:", error);
        onError?.();
        const errorMessage =
          error?.response?.data?.message || "Unable to export variants";
        message.error(errorMessage);
      }
    };

interface ImportVariantsParams {
  file: File;
  productId: string;
  onSuccess: () => void;
  onError: () => void;
}

export const importVariantsAPI =
  ({ productId, file, onSuccess, onError }: ImportVariantsParams) =>
    async (dispatch: any) => {
      try {
        const formData = new FormData();

        formData.append("file", file);
        formData.append("product_id", productId);

        setTimeout(() => {
          onSuccess();
        }, 1000);
        const response = await axiosInstance.post(
          `/products/import-variants`,
          formData,
          {
            headers: {
              // Essential for file uploads
              "Content-Type": "multipart/form-data",
            },
          },
        );

        // 3. Handle Success
        if (response.data?.success) {
          // message.success(
          //   response.data?.message || "Price Sheet imported successfully!",
          // );
          // onSuccess();
        } else {
          message.error(response.data?.message || "Import failed");
          onError();
        }
      } catch (error: any) {
        console.error("Import error:", error);

        const errorMessage =
          error?.response?.data?.message || "Import failed. Please try again.";
        message.error(errorMessage);
        onError();
      }
    };

interface RegenerateVariantsParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const regenerateVariantsAPI =
  ({ payload, onSuccess, onError }: RegenerateVariantsParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.post(
          `/products/regenerate-variants`,
          payload,
        );

        let result = responseDataFormate(response.data);

        if (result?.success) {
          message.success(result?.message);
          onSuccess?.(result.data);
        } else {
          message.error(result?.message);
          onError?.();
        }
      } catch (error: any) {
        console.error("Regenrate Variants error:", error);
        onError?.();

        const errorMessage = error?.response?.data?.message;
        message.error(errorMessage);
      }
    };

interface ReorderProductsParams {
  payload: {
    draggedId: string;
    oldIndex: number;
    newIndex: number;
    filters?: any;
  };
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const reorderProductsAPI =
  ({ payload, onSuccess, onError }: ReorderProductsParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.put(
          `${API_URL}/products/reorder`,
          payload,
        );

        let result = responseDataFormate(response.data);

        if (result?.success) {
          message.success(result?.message || "Products reordered successfully");
          onSuccess?.(result.data);
        } else {
          message.error(result?.message || "Failed to reorder products");
          onError?.();
        }
      } catch (error: any) {
        console.error("Reorder products error:", error);
        onError?.();

        const errorMessage =
          error?.response?.data?.message || "Unable to reorder products";
        message.error(errorMessage);
      }
    };

export interface ProductPartsFilterParams {
  product_id: string;
  search?: string;
}


interface GetProductPartsParams {
  filters: ProductPartsFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const getProductPartsAPI =
  ({ filters, onSuccess, onError, signal }: GetProductPartsParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.get("/products/bom-details/parts", {
          params: filters,
          signal: signal,
        });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          onSuccess?.(result.data);
        } else {
          message.error(result?.message);
          onError?.();
        }
      } catch (error: any) {
        if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
        console.error("Get Products List Error:", error);
        message.error(
          error?.response?.data?.message
        );
        onError?.();
      }
    };

export interface ProductComponentsFilterParams {
  product_id: string;
  search?: string;
}

interface GetProductComponentsParams {
  filters: ProductComponentsFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const getProductComponentsAPI =
  ({ filters, onSuccess, onError, signal }: GetProductComponentsParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.get("/products/bom-details/components", {
          params: filters,
          signal: signal,
        });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          onSuccess?.(result.data);
        } else {
          message.error(result?.message);
          onError?.();
        }
      } catch (error: any) {
        if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
        console.error("Get Product Components Error:", error);
        message.error(
          error?.response?.data?.message
        );
        onError?.();
      }
    };

export interface ProductProcessesFilterParams {
  product_id: string;
  search?: string;
}

interface GetProductProcessesParams {
  filters: ProductProcessesFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const getProductProcessesAPI =
  ({ filters, onSuccess, onError, signal }: GetProductProcessesParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.get("/products/bom-details/processes", {
          params: filters,
          signal: signal,
        });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          onSuccess?.(result.data);
        } else {
          message.error(result?.message);
          onError?.();
        }
      } catch (error: any) {
        if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
        console.error("Get Product Processes Error:", error);
        message.error(
          error?.response?.data?.message
        );
        onError?.();
      }
    };
